/**
 * Pure logic of the product lines screen (port of ProductLinesComponent):
 * keyboard label of a row and sorting by column.
 */
import { KEYBOARDS } from '@/shared/constants';
import { compareCodeUnits } from '@/shared/lib/compare';
import { isKeyboardKey } from '@/shared/config/product-line';
import type { ProductLine } from './api';

/** "Keyboards" cell: the active keyboards, in the order of the product line's `keyboard` object. */
export const keyboardLabel = (
  keyboard: ProductLine['keyboard'] | undefined,
): string =>
  Object.keys(keyboard ?? {})
    .filter(isKeyboardKey)
    .filter((key) => keyboard?.[key]?.active)
    .map((key) => KEYBOARDS[key])
    .join(', ');

export type SortKey =
  | 'productLine'
  | 'commercialProductLine'
  | 'display'
  | 'productGroup'
  | 'sav'
  | 'keyboardLabel';

export interface SortState {
  by: SortKey;
  reverse: boolean;
}

export const DEFAULT_SORT: SortState = { by: 'productLine', reverse: false };

export const nextSort = (current: SortState, by: SortKey): SortState =>
  current.by === by
    ? { by, reverse: !current.reverse }
    : { by, reverse: false };

const NUMERIC_KEYS: readonly SortKey[] = ['display', 'productGroup'];

const rawValue = (item: ProductLine, by: SortKey): unknown =>
  by === 'keyboardLabel' ? keyboardLabel(item.keyboard) : item[by];

/** Number for the numeric columns (NaN when missing), lower-cased text otherwise. */
const sortValue = (item: ProductLine, by: SortKey): number | string =>
  NUMERIC_KEYS.includes(by)
    ? Number(rawValue(item, by) ?? Number.NaN)
    : String(rawValue(item, by) ?? '').toLowerCase();

/**
 * lodash `sortBy` semantics: stable, numbers compared as numbers (NaN last),
 * strings lower-cased; reversed on demand.
 */
export const sortProductLines = (
  items: readonly ProductLine[],
  sort: SortState,
): ProductLine[] => {
  const compare = (a: number | string, b: number | string): number => {
    if (typeof a === 'number' && typeof b === 'number') {
      if (Number.isNaN(a)) return Number.isNaN(b) ? 0 : 1;
      if (Number.isNaN(b)) return -1;
      return a - b;
    }
    return compareCodeUnits(a, b);
  };
  const sorted = [...items].sort((a, b) =>
    compare(sortValue(a, sort.by), sortValue(b, sort.by)),
  );
  return sort.reverse ? sorted.reverse() : sorted;
};
